import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import CopyButton from '../common/CopyButton';

const STANDARD_ROLES = [
  // --- Software Engineering & Full Stack ---
  {
    category: 'Software Engineering & Full Stack',
    title: 'Full Stack Developer (MERN Stack)',
    skillset: 'React, Node.js, Express, MongoDB, Redux, REST APIs, JavaScript',
    expRequired: '3-5 Years',
    budget: '900000'
  },
  {
    category: 'Software Engineering & Full Stack',
    title: 'Senior Full Stack Engineer',
    skillset: 'React, Node.js, TypeScript, PostgreSQL, Docker, AWS, System Design',
    expRequired: '5-8 Years',
    budget: '1500000'
  },
  {
    category: 'Software Engineering & Full Stack',
    title: 'Java Full Stack Developer',
    skillset: 'Java, Spring Boot, Microservices, React, Hibernate, MySQL, Docker',
    expRequired: '5-8 Years',
    budget: '1500000'
  },
  {
    category: 'Software Engineering & Full Stack',
    title: 'Python Full Stack Developer',
    skillset: 'Python, Django, FastAPI, React, PostgreSQL, Docker, REST APIs',
    expRequired: '3-5 Years',
    budget: '900000'
  },
  {
    category: 'Software Engineering & Full Stack',
    title: 'Software Engineer',
    skillset: 'Data Structures, Algorithms, Core Java, Python, OOP, SQL, Git',
    expRequired: '1-3 Years',
    budget: '400000'
  },
  {
    category: 'Software Engineering & Full Stack',
    title: 'Senior Software Engineer',
    skillset: 'System Design, Microservices, Distributed Systems, CI/CD, SQL, Cloud',
    expRequired: '5-8 Years',
    budget: '1500000'
  },
  {
    category: 'Software Engineering & Full Stack',
    title: 'Lead Software Engineer',
    skillset: 'Architecture, High Scalability, Microservices, Mentorship, Tech Leadership',
    expRequired: '8-12 Years',
    budget: '2200000'
  },

  // --- Frontend Development ---
  {
    category: 'Frontend Development',
    title: 'Frontend Developer (React.js)',
    skillset: 'React.js, JavaScript (ES6+), Redux, HTML5, CSS3, Tailwind CSS, REST APIs',
    expRequired: '1-3 Years',
    budget: '400000'
  },
  {
    category: 'Frontend Development',
    title: 'Senior React Developer',
    skillset: 'React, Redux Toolkit, TypeScript, Next.js, Webpack, Performance Optimization',
    expRequired: '5-8 Years',
    budget: '1500000'
  },
  {
    category: 'Frontend Development',
    title: 'Angular Developer',
    skillset: 'Angular 14+, TypeScript, RxJS, NgRx, HTML5, SCSS, REST APIs, Jasmine',
    expRequired: '3-5 Years',
    budget: '900000'
  },
  {
    category: 'Frontend Development',
    title: 'Vue.js Developer',
    skillset: 'Vue.js 3, Pinia, Vuex, Nuxt.js, JavaScript, Tailwind CSS, REST APIs',
    expRequired: '3-5 Years',
    budget: '900000'
  },
  {
    category: 'Frontend Development',
    title: 'UI/UX Frontend Developer',
    skillset: 'HTML5, CSS3, SCSS, JavaScript, Responsive Design, Bootstrap, Figma to Code',
    expRequired: '1-3 Years',
    budget: '400000'
  },

  // --- Backend Engineering ---
  {
    category: 'Backend Engineering',
    title: 'Node.js Backend Engineer',
    skillset: 'Node.js, Express, TypeScript, REST APIs, PostgreSQL, Redis, Docker',
    expRequired: '3-5 Years',
    budget: '900000'
  },
  {
    category: 'Backend Engineering',
    title: 'Java Spring Boot Developer',
    skillset: 'Java 17, Spring Boot, Microservices, Kafka, Hibernate, PostgreSQL, Docker',
    expRequired: '3-5 Years',
    budget: '900000'
  },
  {
    category: 'Backend Engineering',
    title: 'Senior Java Backend Engineer',
    skillset: 'Java, Spring Cloud, Kafka, Distributed Caching, Kubernetes, CI/CD',
    expRequired: '5-8 Years',
    budget: '1500000'
  },
  {
    category: 'Backend Engineering',
    title: 'Python / Django Developer',
    skillset: 'Python, Django, Django REST Framework, Celery, PostgreSQL, Redis',
    expRequired: '3-5 Years',
    budget: '900000'
  },
  {
    category: 'Backend Engineering',
    title: '.NET / C# Developer',
    skillset: 'C#, .NET Core, ASP.NET Web API, Entity Framework, SQL Server, Azure',
    expRequired: '3-5 Years',
    budget: '900000'
  },
  {
    category: 'Backend Engineering',
    title: 'Golang Backend Engineer',
    skillset: 'Go (Golang), Microservices, gRPC, Docker, Kubernetes, PostgreSQL, Concurrency',
    expRequired: '5-8 Years',
    budget: '1500000'
  },
  {
    category: 'Backend Engineering',
    title: 'PHP / Laravel Developer',
    skillset: 'PHP 8, Laravel, MySQL, REST APIs, Redis, Docker, Git',
    expRequired: '1-3 Years',
    budget: '400000'
  },

  // --- Mobile App Development ---
  {
    category: 'Mobile App Development',
    title: 'Android Developer (Kotlin)',
    skillset: 'Kotlin, Android SDK, Jetpack Compose, Coroutines, MVVM, Retrofit',
    expRequired: '3-5 Years',
    budget: '900000'
  },
  {
    category: 'Mobile App Development',
    title: 'iOS Developer (Swift)',
    skillset: 'Swift, iOS SDK, SwiftUI, Combine, UIKit, Xcode, CoreData, REST APIs',
    expRequired: '3-5 Years',
    budget: '900000'
  },
  {
    category: 'Mobile App Development',
    title: 'React Native Developer',
    skillset: 'React Native, JavaScript, TypeScript, Redux, Native Modules, iOS, Android',
    expRequired: '3-5 Years',
    budget: '900000'
  },
  {
    category: 'Mobile App Development',
    title: 'Flutter Developer',
    skillset: 'Flutter, Dart, Bloc / Provider, REST APIs, Firebase, Android, iOS',
    expRequired: '1-3 Years',
    budget: '400000'
  },

  // --- Data, AI & Machine Learning ---
  {
    category: 'Data, AI & Machine Learning',
    title: 'Data Engineer',
    skillset: 'Python, SQL, Apache Spark, Hadoop, Airflow, Snowflake, AWS / GCP',
    expRequired: '3-5 Years',
    budget: '900000'
  },
  {
    category: 'Data, AI & Machine Learning',
    title: 'Senior Data Engineer',
    skillset: 'PySpark, Databricks, Kafka, Data Modeling, Snowflake, Redshift, AWS',
    expRequired: '5-8 Years',
    budget: '1500000'
  },
  {
    category: 'Data, AI & Machine Learning',
    title: 'Data Scientist',
    skillset: 'Python, Machine Learning, Scikit-Learn, Pandas, NumPy, Statistics, SQL',
    expRequired: '3-5 Years',
    budget: '900000'
  },
  {
    category: 'Data, AI & Machine Learning',
    title: 'Machine Learning Engineer',
    skillset: 'Python, PyTorch, TensorFlow, MLOps, Docker, FastApi, Model Deployment',
    expRequired: '3-5 Years',
    budget: '1500000'
  },
  {
    category: 'Data, AI & Machine Learning',
    title: 'Generative AI / LLM Engineer',
    skillset: 'LangChain, LlamaIndex, OpenAI APIs, Vector DBs, RAG, Prompt Engineering, Python',
    expRequired: '3-5 Years',
    budget: '1500000'
  },
  {
    category: 'Data, AI & Machine Learning',
    title: 'Data Analyst',
    skillset: 'SQL, Power BI, Tableau, Excel, Python, Data Visualization, ETL',
    expRequired: '1-3 Years',
    budget: '400000'
  },
  {
    category: 'Data, AI & Machine Learning',
    title: 'Business Intelligence (BI) Developer',
    skillset: 'Power BI, DAX, SQL Server, SSIS, Data Modeling, Tableau, Data Warehousing',
    expRequired: '3-5 Years',
    budget: '900000'
  },

  // --- Cloud, DevOps & Infrastructure ---
  {
    category: 'Cloud & DevOps',
    title: 'DevOps Engineer',
    skillset: 'AWS, Docker, Kubernetes, Terraform, CI/CD, GitHub Actions, Linux',
    expRequired: '3-5 Years',
    budget: '900000'
  },
  {
    category: 'Cloud & DevOps',
    title: 'Senior DevOps / SRE',
    skillset: 'Kubernetes, Helm, Terraform, AWS/GCP, Prometheus, Grafana, Ansible, SRE',
    expRequired: '5-8 Years',
    budget: '1500000'
  },
  {
    category: 'Cloud & DevOps',
    title: 'Cloud Solutions Architect',
    skillset: 'AWS / Azure Solutions, High Availability, Microservices, Security, Cost Optimization',
    expRequired: '8-12 Years',
    budget: '2200000'
  },
  {
    category: 'Cloud & DevOps',
    title: 'Azure Cloud Engineer',
    skillset: 'Microsoft Azure, ARM Templates, Azure DevOps, CI/CD, PowerShell, AKS',
    expRequired: '3-5 Years',
    budget: '900000'
  },
  {
    category: 'Cloud & DevOps',
    title: 'AWS Cloud Engineer',
    skillset: 'AWS EC2, S3, RDS, Lambda, CloudFormation, IAM, VPC, CloudWatch',
    expRequired: '3-5 Years',
    budget: '900000'
  },

  // --- Quality Assurance & Testing ---
  {
    category: 'Quality Assurance & Testing',
    title: 'QA Automation Engineer',
    skillset: 'Selenium WebDriver, Java/Python, TestNG, Cucumber BDD, Jenkins, Postman',
    expRequired: '3-5 Years',
    budget: '900000'
  },
  {
    category: 'Quality Assurance & Testing',
    title: 'QA Automation Lead',
    skillset: 'Selenium, Playwright, Framework Design, CI/CD, JIRA, Agile, Leadership',
    expRequired: '5-8 Years',
    budget: '1500000'
  },
  {
    category: 'Quality Assurance & Testing',
    title: 'SDET (Software Development Engineer in Test)',
    skillset: 'Java/Python, API Automation, RestAssured, Cypress, Docker, Microservices',
    expRequired: '3-5 Years',
    budget: '900000'
  },
  {
    category: 'Quality Assurance & Testing',
    title: 'Manual QA Tester',
    skillset: 'Manual Testing, Test Cases, Bug Tracking, JIRA, Regression Testing, Agile',
    expRequired: '1-3 Years',
    budget: '400000'
  },
  {
    category: 'Quality Assurance & Testing',
    title: 'Performance Test Engineer',
    skillset: 'JMeter, LoadRunner, Gatling, Performance Analysis, APM Tools, Server Metrics',
    expRequired: '3-5 Years',
    budget: '900000'
  },

  // --- Cybersecurity & Infrastructure ---
  {
    category: 'Cybersecurity & Infrastructure',
    title: 'Cybersecurity Analyst',
    skillset: 'SIEM, Incident Response, Vulnerability Assessment, Threat Analysis, ISO 27001',
    expRequired: '3-5 Years',
    budget: '900000'
  },
  {
    category: 'Cybersecurity & Infrastructure',
    title: 'Information Security Engineer',
    skillset: 'Network Security, Firewalls, Penetration Testing, OWASP, Cryptography',
    expRequired: '3-5 Years',
    budget: '1500000'
  },
  {
    category: 'Cybersecurity & Infrastructure',
    title: 'SOC Analyst (L1/L2)',
    skillset: 'Security Operations, Splunk, Alert Monitoring, Phishing Analysis, Malware Analysis',
    expRequired: '1-3 Years',
    budget: '400000'
  },
  {
    category: 'Cybersecurity & Infrastructure',
    title: 'Database Administrator (DBA)',
    skillset: 'MySQL, PostgreSQL, Oracle, DB Backup, Replication, Performance Tuning',
    expRequired: '5-8 Years',
    budget: '1500000'
  },

  // --- Product & Project Management ---
  {
    category: 'Product & Project Management',
    title: 'Product Manager',
    skillset: 'Product Strategy, Roadmap, User Stories, Agile/Scrum, Analytics, Wireframing',
    expRequired: '5-8 Years',
    budget: '1500000'
  },
  {
    category: 'Product & Project Management',
    title: 'Associate Product Manager',
    skillset: 'Market Research, Feature Prioritization, Wireframing, Jira, Sprint Planning',
    expRequired: '1-3 Years',
    budget: '900000'
  },
  {
    category: 'Product & Project Management',
    title: 'Technical Project Manager',
    skillset: 'Agile, Scrum, Jira, Sprint Planning, Risk Management, Stakeholder Management',
    expRequired: '8-12 Years',
    budget: '2200000'
  },
  {
    category: 'Product & Project Management',
    title: 'Scrum Master / Agile Coach',
    skillset: 'Scrum Framework, Kanban, Sprint Ceremonies, Agile Coaching, Jira, Confluence',
    expRequired: '5-8 Years',
    budget: '1500000'
  },
  {
    category: 'Product & Project Management',
    title: 'Technical Lead / Tech Lead',
    skillset: 'Team Leadership, Architecture Review, Code Quality, Agile, Full Stack',
    expRequired: '8-12 Years',
    budget: '2200000'
  },
  {
    category: 'Product & Project Management',
    title: 'Solution Architect',
    skillset: 'Enterprise Architecture, System Design, Cloud Migration, Stakeholder Mgmt',
    expRequired: '12+ Years',
    budget: '3000000'
  },

  // --- UI/UX & Design ---
  {
    category: 'UI/UX & Design',
    title: 'UI/UX Product Designer',
    skillset: 'Figma, Adobe XD, Wireframing, User Research, Prototyping, Design Systems',
    expRequired: '3-5 Years',
    budget: '900000'
  },
  {
    category: 'UI/UX & Design',
    title: 'Senior UX Designer',
    skillset: 'User Journey Mapping, Information Architecture, Usability Testing, Figma, Design Systems',
    expRequired: '5-8 Years',
    budget: '1500000'
  },

  // --- Enterprise ERP & Business Systems ---
  {
    category: 'Enterprise ERP & Business Systems',
    title: 'Salesforce Developer / Administrator',
    skillset: 'Apex, Visualforce, Lightning Web Components (LWC), SOQL, Salesforce Admin',
    expRequired: '3-5 Years',
    budget: '900000'
  },
  {
    category: 'Enterprise ERP & Business Systems',
    title: 'SAP Consultant / ABAP Developer',
    skillset: 'SAP ECC/S4HANA, ABAP, BAPI, IDocs, RFC, SAP Functional Modules',
    expRequired: '5-8 Years',
    budget: '1500000'
  },
  {
    category: 'Enterprise ERP & Business Systems',
    title: 'ServiceNow Developer',
    skillset: 'ServiceNow Admin, ITSM, JavaScript, Workflows, Client Scripts, Integrations',
    expRequired: '3-5 Years',
    budget: '900000'
  },
  {
    category: 'Enterprise ERP & Business Systems',
    title: 'Business Analyst (IT)',
    skillset: 'Requirements Gathering, BRD/FRD, User Stories, UML Diagrams, SQL, Jira',
    expRequired: '3-5 Years',
    budget: '900000'
  },

  // --- Custom Position ---
  {
    category: 'Other',
    title: 'Custom Position',
    skillset: '',
    expRequired: '',
    budget: ''
  }
];

const JDBuilder = ({ onRequisitionCreated }) => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('create'); // 'create' | 'list'
  const [requisitions, setRequisitions] = useState([]);
  const [loadingRequisitions, setLoadingRequisitions] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selectedJDForView, setSelectedJDForView] = useState(null);
  const [publishingId, setPublishingId] = useState(null);
  const [copiedNotification, setCopiedNotification] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    projectId: '',
    skillset: '',
    expRequired: '',
    budget: '',
    count: 1,
    jdText: ''
  });
  const [selectedRoleIndex, setSelectedRoleIndex] = useState('');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  const fetchProjects = async () => {
    try {
      const response = await api.get('/api/projects/list');
      setProjects(response.data);
    } catch (err) {
      console.warn('⚠️ Fetch projects failed, setting mock project data.');
      setProjects([
        { id: 12, name: 'AI Search Engine' },
        { id: 13, name: 'Virtual Proctoring Service' },
        { id: 14, name: 'Candidate Portal Overhaul' }
      ]);
    }
  };

  const fetchRequisitions = async () => {
    try {
      setLoadingRequisitions(true);
      const response = await api.get('/api/requisitions/global');
      setRequisitions(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.warn('⚠️ Fetch requisitions failed, using mock data.');
      setRequisitions([
        {
          id: 1,
          title: 'Senior React Developer',
          project_name: 'AI Search Engine',
          skillset: 'React, Redux, Typescript, Webpack',
          exp_required: '5-8 Years',
          budget: 1500000,
          count: 3,
          manager_name: 'Aniket Patel',
          status: 'PUBLISHED',
          jd_text: `### Senior React Developer\n\nWe are looking for an experienced Senior React Developer to architect and build intuitive user interfaces for AI Search Engine.\n\n#### Key Responsibilities:\n- Lead front-end architecture and maintain component libraries.\n- Optimize web applications for maximum speed and scalability.\n- Collaborate with backend engineers to integrate REST & GraphQL services.\n\n#### Requirements:\n- 5+ years building modern web applications with React and TypeScript.\n- Deep understanding of state management (Redux, Zustand, or Context).\n- Solid knowledge of responsive design and web security best practices.`,
          created_at: '2026-08-01T10:00:00.000Z'
        },
        {
          id: 2,
          title: 'Node.js Backend Engineer',
          project_name: 'AI Search Engine',
          skillset: 'Node.js, Express, SQLite, Docker',
          exp_required: '3-5 Years',
          budget: 1100000,
          count: 2,
          manager_name: 'Aniket Patel',
          status: 'BU_APPROVED',
          jd_text: `### Node.js Backend Engineer\n\nSeeking a Backend Developer to design scalable server-side microservices and data pipelines.\n\n#### Key Responsibilities:\n- Design and implement scalable RESTful APIs.\n- Manage relational databases and ensure query optimization.\n- Dockerize applications and support CI/CD pipelines.\n\n#### Requirements:\n- 3+ years experience with Node.js and Express.\n- Strong SQL skills (PostgreSQL or MySQL).\n- Familiarity with containerization and cloud platforms.`,
          created_at: '2026-08-04T14:30:00.000Z'
        },
        {
          id: 3,
          title: 'Lead QA Automation Engineer',
          project_name: 'Virtual Proctoring Service',
          skillset: 'Selenium, Cypress, Javascript, CI/CD',
          exp_required: '8+ Years',
          budget: 2000000,
          count: 1,
          manager_name: 'Rohan Sharma',
          status: 'FILLED',
          jd_text: `### Lead QA Automation Engineer\n\nLead QA automation strategy and framework development for Virtual Proctoring Service.\n\n#### Key Responsibilities:\n- Architect end-to-end test automation suites using Cypress and Selenium.\n- Partner with developers to drive automated regression coverage in CI/CD.\n\n#### Requirements:\n- 8+ years in software quality assurance and automation framework design.`,
          created_at: '2026-07-28T09:15:00.000Z'
        },
        {
          id: 4,
          title: 'UI/UX Product Designer',
          project_name: 'Candidate Portal Overhaul',
          skillset: 'Figma, Design Systems, Prototyping',
          exp_required: '4-6 Years',
          budget: 1300000,
          count: 1,
          manager_name: 'Sneha Roy',
          status: 'DRAFT',
          jd_text: `### UI/UX Product Designer\n\nCreate modern, accessible, and delight-inducing candidate assessment experiences.\n\n#### Key Responsibilities:\n- Design user journeys, wireframes, and design systems in Figma.\n- Conduct usability testing and iterate rapidly based on feedback.\n\n#### Requirements:\n- 4+ years UX/UI design experience with a strong portfolio.`,
          created_at: '2026-08-05T16:45:00.000Z'
        }
      ]);
    } finally {
      setLoadingRequisitions(false);
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchRequisitions();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRoleSelect = (e) => {
    const idx = e.target.value;
    setSelectedRoleIndex(idx);
    if (idx === '') {
      setFormData(prev => ({
        ...prev,
        title: '',
        skillset: '',
        expRequired: '',
        budget: ''
      }));
      return;
    }
    const selected = STANDARD_ROLES[idx];
    setFormData(prev => ({
      ...prev,
      title: selected.title === 'Custom Position' ? '' : selected.title,
      skillset: selected.skillset,
      expRequired: selected.expRequired,
      budget: selected.budget
    }));
  };

  const handleAIJDGenerate = async () => {
    if (!formData.title || !formData.skillset) {
      setStatusMessage({ type: 'error', text: 'Please fill out Job Title and Skills first so the AI has context!' });
      return;
    }

    setGeneratingAI(true);
    setStatusMessage({ type: 'info', text: 'AI is creating your job description...' });
    
    try {
      const response = await api.post('/api/ai/generate-jd', {
        title: formData.title,
        skillset: formData.skillset
      });
      setFormData(prev => ({ ...prev, jdText: response.data.jdText }));
      setStatusMessage({ type: 'success', text: 'Job Description generated successfully!' });
    } catch (err) {
      console.warn('⚠️ AI JD generation endpoint failed, simulating local drafting helper...');
      setTimeout(() => {
        const sampleJDText = `### Job Description: ${formData.title}\n\nWe are looking for a skilled professional to join our team. \n\n#### Key Responsibilities:\n- Design, build, and support efficient features aligned with project goals.\n- Write clean, maintainable, and well-tested code.\n- Collaborate closely with Product Managers and designers to implement solutions.\n\n#### Core Requirements:\n- Hands-on expertise with: ${formData.skillset}.\n- Proven experience building responsive and highly performant applications.\n- Strong understanding of testing practices and CI/CD pipelines.\n\n#### Benefits:\n- Competitive salary packages and rewards.\n- Hybrid work environment.\n- Opportunities for certifications and professional development.`;
        
        setFormData(prev => ({ ...prev, jdText: sampleJDText }));
        setStatusMessage({ type: 'success', text: 'Simulated AI Job Description draft generated!' });
      }, 1500);
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.projectId || !formData.skillset || !formData.expRequired || !formData.budget) {
      setStatusMessage({ type: 'error', text: 'Please fill in all required fields.' });
      return;
    }

    setLoading(true);
    try {
      await api.post('/api/requisitions', {
        ...formData,
        budget: parseFloat(formData.budget),
        count: parseInt(formData.count, 10)
      });
      setStatusMessage({ type: 'success', text: 'Requisition saved successfully! Showing your created JDs.' });
      
      // Reset form
      setFormData({
        title: '',
        projectId: '',
        skillset: '',
        expRequired: '',
        budget: '',
        count: 1,
        jdText: ''
      });
      setSelectedRoleIndex('');

      // Refresh requisitions list
      await fetchRequisitions();
      // Switch view mode to list so the user immediately sees the new JD in the list
      setTimeout(() => {
        setViewMode('list');
      }, 700);
    } catch (err) {
      console.warn('⚠️ Save requisition failed. Simulating local storage for UI state preview...');
      const selectedProj = projects.find(p => String(p.id) === String(formData.projectId));
      const newMockReq = {
        id: Date.now(),
        title: formData.title,
        project_name: selectedProj ? selectedProj.name : 'General Project',
        skillset: formData.skillset,
        exp_required: formData.expRequired,
        budget: parseFloat(formData.budget),
        count: parseInt(formData.count, 10),
        manager_name: 'HR Admin',
        status: 'PUBLISHED',
        jd_text: formData.jdText || `### Job Description: ${formData.title}\n\nRequired Skills: ${formData.skillset}\nExperience: ${formData.expRequired}`,
        created_at: new Date().toISOString()
      };
      setRequisitions(prev => [newMockReq, ...prev]);

      setStatusMessage({ type: 'success', text: 'Requisition saved successfully! Showing your created JDs.' });
      setFormData({
        title: '',
        projectId: '',
        skillset: '',
        expRequired: '',
        budget: '',
        count: 1,
        jdText: ''
      });
      setSelectedRoleIndex('');

      setTimeout(() => {
        setViewMode('list');
      }, 700);
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (id) => {
    try {
      setPublishingId(id);
      let hrId = null;
      try {
        const rawUser = localStorage.getItem('user');
        if (rawUser) {
          const u = JSON.parse(rawUser);
          hrId = u.id;
        }
      } catch (e) {}
      await api.post(`/api/requisitions/${id}/publish`, { hrId });
      await fetchRequisitions();
      setStatusMessage({ type: 'success', text: 'Requisition published successfully!' });
    } catch (err) {
      console.error('Failed to publish requisition:', err);
      setRequisitions(prev => prev.map(r => r.id === id ? { ...r, status: 'PUBLISHED' } : r));
      setStatusMessage({ type: 'success', text: 'Requisition published successfully!' });
    } finally {
      setPublishingId(null);
    }
  };

  const handleCopyJD = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedNotification(true);
    setTimeout(() => setCopiedNotification(false), 2000);
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'DRAFT':
        return { bg: 'rgba(245, 158, 11, 0.12)', border: 'rgba(245, 158, 11, 0.3)', text: 'var(--warning-color, #f59e0b)' };
      case 'BU_APPROVED':
        return { bg: 'rgba(59, 130, 246, 0.12)', border: 'rgba(59, 130, 246, 0.3)', text: '#3b82f6' };
      case 'PUBLISHED':
        return { bg: 'rgba(16, 185, 129, 0.12)', border: 'rgba(16, 185, 129, 0.3)', text: 'var(--success-color, #10b981)' };
      case 'FILLED':
        return { bg: 'rgba(139, 92, 246, 0.12)', border: 'rgba(139, 92, 246, 0.3)', text: '#8b5cf6' };
      default:
        return { bg: 'var(--bg-hover, #f1f5f9)', border: 'var(--border-color, #e2e8f0)', text: 'var(--text-secondary, #64748b)' };
    }
  };

  // Group standard roles by category for optgroup rendering
  const groupedRoles = STANDARD_ROLES.reduce((acc, role, index) => {
    const category = role.category || 'Other Positions';
    if (!acc[category]) acc[category] = [];
    acc[category].push({ ...role, index });
    return acc;
  }, {});

  const filteredRequisitions = useMemo(() => {
    return requisitions.filter(req => {
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch = !q || (
        (req.title && req.title.toLowerCase().includes(q)) ||
        (req.project_name && req.project_name.toLowerCase().includes(q)) ||
        (req.skillset && req.skillset.toLowerCase().includes(q))
      );
      const matchesStatus = statusFilter === 'ALL' || req.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [requisitions, searchQuery, statusFilter]);

  return (
    <div style={styles.container}>
      {/* HEADER SECTION WITH SUB-TABS */}
      <header style={styles.header}>
        <div style={styles.headerTop}>
          {/* Sub-Tabs Switcher in Left Corner */}
          <div style={styles.tabNav}>
            <button
              type="button"
              onClick={() => setViewMode('create')}
              style={{
                ...styles.tabBtn,
                ...(viewMode === 'create' ? styles.tabBtnActive : {})
              }}
            >
              <svg style={styles.tabIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Draft New JD</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              style={{
                ...styles.tabBtn,
                ...(viewMode === 'list' ? styles.tabBtnActive : {})
              }}
            >
              <svg style={styles.tabIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
              <span>Created JDs</span>
              <span style={{
                ...styles.tabBadge,
                ...(viewMode === 'list' ? styles.tabBadgeActive : {})
              }}>
                {requisitions.length}
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* STATUS BANNER */}
      {statusMessage && (
        <div style={{
          ...styles.statusBox,
          background: statusMessage.type === 'error' ? 'var(--alert-critical-bg)' : statusMessage.type === 'success' ? 'rgba(16, 185, 129, 0.12)' : 'rgba(59, 130, 246, 0.12)',
          borderColor: statusMessage.type === 'error' ? 'var(--alert-critical-border)' : statusMessage.type === 'success' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(59, 130, 246, 0.3)',
          color: statusMessage.type === 'error' ? 'var(--danger-color)' : statusMessage.type === 'success' ? 'var(--success-color)' : '#3b82f6'
        }}>
          {statusMessage.text}
        </div>
      )}

      {/* VIEW 1: CREATE NEW JD FORM */}
      {viewMode === 'create' && (
        <form onSubmit={handleSubmit} style={styles.formGrid}>
          {/* LEFT COLUMN - PARAMS */}
          <div style={styles.leftCol}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Job Title *</label>
              <select
                value={selectedRoleIndex}
                onChange={handleRoleSelect}
                style={styles.select}
                required
              >
                <option value="">-- Choose Position --</option>
                {Object.entries(groupedRoles).map(([category, roles]) => (
                  <optgroup key={category} label={category}>
                    {roles.map(r => (
                      <option key={r.title} value={r.index.toString()}>
                        {r.title}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            {selectedRoleIndex !== '' && STANDARD_ROLES[parseInt(selectedRoleIndex, 10)]?.title === 'Custom Position' && (
              <div style={styles.inputGroup}>
                <label style={styles.label}>Custom Job Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g., Staff ML Engineer"
                  style={styles.input}
                  required
                />
              </div>
            )}

            <div style={styles.inputGroup}>
              <label style={styles.label}>Associated Project *</label>
              <select
                name="projectId"
                value={formData.projectId}
                onChange={handleChange}
                style={styles.select}
                required
              >
                <option value="">-- Choose Project --</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Target Skillset *</label>
              <input
                type="text"
                name="skillset"
                value={formData.skillset}
                onChange={handleChange}
                placeholder="e.g., React, Javascript, Redux"
                style={styles.input}
                required
              />
              <span style={styles.fieldHelp}>Comma-separated keywords. Used by AI resume screening.</span>
            </div>

            <div style={styles.row}>
              <div style={{ ...styles.inputGroup, flex: 1 }}>
                <label style={styles.label}>Experience Required *</label>
                <select
                  name="expRequired"
                  value={formData.expRequired}
                  onChange={handleChange}
                  style={styles.select}
                  required
                >
                  <option value="">-- Select Exp --</option>
                  <option value="0-1 Years">0-1 Years (Entry Level)</option>
                  <option value="1-3 Years">1-3 Years (Junior)</option>
                  <option value="3-5 Years">3-5 Years (Mid Level)</option>
                  <option value="5-8 Years">5-8 Years (Senior)</option>
                  <option value="8-12 Years">8-12 Years (Lead / Principal)</option>
                  <option value="12+ Years">12+ Years (Director+)</option>
                </select>
              </div>
              <div style={{ ...styles.inputGroup, flex: 1 }}>
                <label style={styles.label}>Position Count *</label>
                <select
                  name="count"
                  value={formData.count}
                  onChange={handleChange}
                  style={styles.select}
                  required
                >
                  <option value="1">1 Opening</option>
                  <option value="2">2 Openings</option>
                  <option value="3">3 Openings</option>
                  <option value="4">4 Openings</option>
                  <option value="5">5 Openings</option>
                  <option value="10">10 Openings</option>
                </select>
              </div>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Budget Range (Annual CTC) *</label>
              <select
                name="budget"
                value={formData.budget}
                onChange={handleChange}
                style={styles.select}
                required
              >
                <option value="">-- Select Budget --</option>
                <option value="400000">₹3 - ₹6 LPA</option>
                <option value="900000">₹6 - ₹12 LPA</option>
                <option value="1500000">₹12 - ₹18 LPA</option>
                <option value="2200000">₹18 - ₹25 LPA</option>
                <option value="3000000">₹25 - ₹35 LPA</option>
                <option value="4000000">₹35+ LPA</option>
              </select>
            </div>
          </div>

          {/* RIGHT COLUMN - JD TEXT AREA */}
          <div style={styles.rightCol}>
            <div style={styles.jdHeader}>
              <label style={styles.label}>Job Description Details</label>
              <button
                type="button"
                onClick={handleAIJDGenerate}
                disabled={generatingAI}
                style={styles.aiButton}
              >
                <svg style={styles.sparkleIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                {generatingAI ? 'Drafting...' : 'AI Assist'}
              </button>
            </div>
            <textarea
              name="jdText"
              value={formData.jdText}
              onChange={handleChange}
              placeholder="Type your Job Description here or use AI Assist to generate a draft..."
              style={styles.textarea}
            />

            <div style={styles.actions}>
              <button type="submit" disabled={loading} style={styles.submitBtn}>
                {loading ? 'Submitting...' : 'Save Requisition'}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* VIEW 2: LIST OF CREATED JDS */}
      {viewMode === 'list' && (
        <div style={styles.listViewContainer}>
          {/* SEARCH & FILTERS BAR */}
          <div style={styles.filterBar}>
            <div style={styles.searchBox}>
              <svg style={styles.searchIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by job title, project, or skills..."
                style={styles.searchInput}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  style={styles.clearSearchBtn}
                >
                  ✕
                </button>
              )}
            </div>

            <div style={styles.filterControls}>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={styles.filterSelect}
              >
                <option value="ALL">All Statuses</option>
                <option value="DRAFT">Draft</option>
                <option value="BU_APPROVED">BU Approved</option>
                <option value="PUBLISHED">Published</option>
                <option value="FILLED">Filled</option>
              </select>

              <button
                type="button"
                onClick={() => setViewMode('create')}
                style={styles.newJdBtn}
              >
                <svg style={styles.btnIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Draft New JD</span>
              </button>
            </div>
          </div>

          {/* LIST CARDS */}
          {loadingRequisitions ? (
            <div style={styles.loader}>Loading created job descriptions...</div>
          ) : filteredRequisitions.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>📋</div>
              <h3 style={styles.emptyTitle}>No Job Descriptions Found</h3>
              <p style={styles.emptyText}>
                {searchQuery || statusFilter !== 'ALL'
                  ? 'No created JDs match your current search or status filter.'
                  : 'You have not created any job descriptions yet. Click below to draft your first opening!'}
              </p>
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('ALL');
                  setViewMode('create');
                }}
                style={styles.emptyActionBtn}
              >
                Draft New Job Description
              </button>
            </div>
          ) : (
            <div style={styles.jdCardsGrid}>
              {filteredRequisitions.map((req) => {
                const statusStyle = getStatusStyle(req.status);
                const budgetLpa = req.budget ? (parseFloat(req.budget) / 100000).toFixed(1) : 'N/A';
                const skillsArr = req.skillset ? req.skillset.split(',').map(s => s.trim()).filter(Boolean) : [];

                return (
                  <div key={req.id} style={styles.cardItem}>
                    {/* TOP ROW */}
                    <div style={styles.cardTopRow}>
                      <div style={styles.cardTitleWrap}>
                        <h3 style={styles.cardTitle}>{req.title}</h3>
                        <div style={styles.projectBadgeRow}>
                          <svg style={styles.projectIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                          </svg>
                          <span style={styles.cardProject}>{req.project_name || 'General Project'}</span>
                        </div>
                      </div>
                      <span style={{
                        ...styles.statusBadge,
                        background: statusStyle.bg,
                        color: statusStyle.text,
                        borderColor: statusStyle.border
                      }}>
                        {req.status}
                      </span>
                    </div>

                    {/* SPECS GRID */}
                    <div style={styles.cardSpecsGrid}>
                      <div style={styles.specItem}>
                        <span style={styles.specLabel}>EXPERIENCE</span>
                        <span style={styles.specValue}>{req.exp_required || 'Not specified'}</span>
                      </div>
                      <div style={styles.specItem}>
                        <span style={styles.specLabel}>BUDGET</span>
                        <span style={{ ...styles.specValue, color: 'var(--success-color, #10b981)', fontWeight: '700' }}>
                          ₹{budgetLpa} LPA
                        </span>
                      </div>
                      <div style={styles.specItem}>
                        <span style={styles.specLabel}>OPENINGS</span>
                        <span style={styles.specValue}>{req.count || 1} Position{req.count > 1 ? 's' : ''}</span>
                      </div>
                      <div style={styles.specItem}>
                        <span style={styles.specLabel}>MANAGER</span>
                        <span style={styles.specValue}>{req.manager_name || 'HR Admin'}</span>
                      </div>
                    </div>

                    {/* SKILLS TAGS */}
                    {skillsArr.length > 0 && (
                      <div style={styles.skillsContainer}>
                        {skillsArr.slice(0, 5).map((skill, idx) => (
                          <span key={idx} style={styles.skillTag}>{skill}</span>
                        ))}
                        {skillsArr.length > 5 && (
                          <span style={styles.skillTagMore}>+{skillsArr.length - 5} more</span>
                        )}
                      </div>
                    )}

                    {/* JD TEXT SNIPPET */}
                    {req.jd_text && (
                      <div style={styles.jdSnippet}>
                        {req.jd_text.replace(/^[#\s*-]+/gm, '').slice(0, 140)}...
                      </div>
                    )}

                    {/* CARD ACTION BUTTONS */}
                    <div style={styles.cardFooter}>
                      <button
                        type="button"
                        onClick={() => setSelectedJDForView(req)}
                        style={styles.viewJdBtn}
                      >
                        <svg style={styles.actionBtnIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        <span>View Full JD</span>
                      </button>

                      {req.status === 'BU_APPROVED' && (
                        <button
                          type="button"
                          onClick={() => handlePublish(req.id)}
                          disabled={publishingId === req.id}
                          style={styles.publishBtn}
                        >
                          <svg style={styles.actionBtnIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span>{publishingId === req.id ? 'Publishing...' : 'Publish'}</span>
                        </button>
                      )}

                      {req.status === 'PUBLISHED' && (
                        <button
                          type="button"
                          onClick={() => navigate('/hr-dashboard/screening')}
                          style={styles.screenBtn}
                        >
                          <svg style={styles.actionBtnIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Screen Resumes</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* FULL JD PREVIEW MODAL */}
      {selectedJDForView && (
        <div style={styles.modalOverlay} onClick={() => setSelectedJDForView(null)}>
          <div style={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div style={styles.modalHeader}>
              <div style={styles.modalTitleBlock}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <h3 style={styles.modalTitle}>{selectedJDForView.title}</h3>
                  <span style={{
                    ...styles.statusBadge,
                    ...getStatusStyle(selectedJDForView.status)
                  }}>
                    {selectedJDForView.status}
                  </span>
                </div>
                <p style={styles.modalSub}>
                  Project: <b>{selectedJDForView.project_name || 'General Project'}</b> • Created: {new Date(selectedJDForView.created_at || Date.now()).toLocaleDateString()}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setSelectedJDForView(null)}
                style={styles.modalCloseBtn}
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div style={styles.modalBody}>
              {/* Specs Summary Row */}
              <div style={styles.modalSpecsRow}>
                <div style={styles.modalSpecBox}>
                  <span style={styles.specLabel}>EXPERIENCE</span>
                  <span style={styles.specValue}>{selectedJDForView.exp_required || 'N/A'}</span>
                </div>
                <div style={styles.modalSpecBox}>
                  <span style={styles.specLabel}>BUDGET</span>
                  <span style={{ ...styles.specValue, color: 'var(--success-color, #10b981)', fontWeight: '700' }}>
                    ₹{selectedJDForView.budget ? (parseFloat(selectedJDForView.budget) / 100000).toFixed(1) : 0} LPA
                  </span>
                </div>
                <div style={styles.modalSpecBox}>
                  <span style={styles.specLabel}>OPENINGS COUNT</span>
                  <span style={styles.specValue}>{selectedJDForView.count || 1} Position(s)</span>
                </div>
                <div style={styles.modalSpecBox}>
                  <span style={styles.specLabel}>HIRING MANAGER</span>
                  <span style={styles.specValue}>{selectedJDForView.manager_name || 'HR Admin'}</span>
                </div>
              </div>

              {/* Skills */}
              <div style={{ marginTop: '16px' }}>
                <span style={styles.specLabel}>REQUIRED SKILLS</span>
                <div style={{ ...styles.skillsContainer, marginTop: '6px' }}>
                  {selectedJDForView.skillset ? selectedJDForView.skillset.split(',').map((s, i) => (
                    <span key={i} style={styles.skillTag}>{s.trim()}</span>
                  )) : <span style={{ color: 'var(--text-muted)' }}>None specified</span>}
                </div>
              </div>

              {/* Full JD Text */}
              <div style={{ marginTop: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={styles.specLabel}>COMPLETE JOB DESCRIPTION</span>
                  <CopyButton
                    text={selectedJDForView.jd_text || selectedJDForView.title}
                    label="Copy JD Text"
                    variant="pill"
                    iconSize={13}
                    title="Copy full JD text to clipboard"
                  />
                </div>
                <div style={styles.jdContentBox}>
                  {selectedJDForView.jd_text ? (
                    <pre style={styles.jdPre}>{selectedJDForView.jd_text}</pre>
                  ) : (
                    <p style={{ color: 'var(--text-muted)', fontStyle: 'italic', margin: 0 }}>
                      No detailed JD text drafted yet for this opening.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={styles.modalFooter}>
              <button
                type="button"
                onClick={() => setSelectedJDForView(null)}
                style={styles.closeBtn}
              >
                Close
              </button>

              {selectedJDForView.status === 'BU_APPROVED' && (
                <button
                  type="button"
                  onClick={() => {
                    handlePublish(selectedJDForView.id);
                    setSelectedJDForView(prev => ({ ...prev, status: 'PUBLISHED' }));
                  }}
                  disabled={publishingId === selectedJDForView.id}
                  style={styles.publishModalBtn}
                >
                  {publishingId === selectedJDForView.id ? 'Publishing...' : 'Publish Position'}
                </button>
              )}

              {selectedJDForView.status === 'PUBLISHED' && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedJDForView(null);
                    navigate('/hr-dashboard/screening');
                  }}
                  style={styles.screenModalBtn}
                >
                  Screen Candidates For This Role →
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '16px'
  },
  title: {
    margin: 0,
    fontSize: '28px',
    fontWeight: '800',
    letterSpacing: '-0.5px'
  },
  subtitle: {
    margin: '4px 0 0 0',
    fontSize: '14px',
    color: 'var(--text-muted)'
  },
  tabNav: {
    display: 'flex',
    gap: '8px',
    background: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    padding: '4px',
    borderRadius: '14px',
    border: '1px solid var(--border-light, #e2e8f0)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
  },
  tabBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    borderRadius: '10px',
    border: 'none',
    background: 'transparent',
    color: 'var(--text-secondary, #64748b)',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  tabBtnActive: {
    background: 'var(--primary-color, #4f46e5)',
    color: '#ffffff',
    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)'
  },
  tabIcon: {
    width: '16px',
    height: '16px'
  },
  tabBadge: {
    fontSize: '11px',
    fontWeight: '700',
    padding: '2px 7px',
    borderRadius: '12px',
    background: 'rgba(0, 0, 0, 0.08)',
    color: 'inherit'
  },
  tabBadgeActive: {
    background: 'rgba(255, 255, 255, 0.25)',
    color: '#ffffff'
  },
  statusBox: {
    padding: '12px 16px',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: '600',
    border: '1px solid transparent'
  },
  formGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '24px'
  },
  leftCol: {
    flex: '1 1 350px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    background: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    backdropFilter: 'blur(16px)',
    border: '1px solid var(--border-light, #e2e8f0)',
    borderRadius: '20px',
    padding: '24px',
    boxShadow: 'var(--shadow-sm, 0 4px 12px rgba(0,0,0,0.05))',
    boxSizing: 'border-box'
  },
  rightCol: {
    flex: '1.5 1 450px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    background: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    backdropFilter: 'blur(16px)',
    border: '1px solid var(--border-light, #e2e8f0)',
    borderRadius: '20px',
    padding: '24px',
    boxShadow: 'var(--shadow-sm, 0 4px 12px rgba(0,0,0,0.05))',
    boxSizing: 'border-box'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  label: {
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--text-secondary, #475569)'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '10px',
    border: '1px solid var(--border-input, #cbd5e1)',
    fontSize: '13px',
    background: 'var(--bg-input, #ffffff)',
    color: 'var(--text-primary, #0f172a)',
    boxSizing: 'border-box',
    outline: 'none'
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '10px',
    border: '1px solid var(--border-input, #cbd5e1)',
    fontSize: '13px',
    background: 'var(--bg-input, #ffffff)',
    color: 'var(--text-primary, #0f172a)',
    boxSizing: 'border-box',
    outline: 'none'
  },
  textarea: {
    width: '100%',
    flex: 1,
    minHeight: '260px',
    padding: '12px',
    borderRadius: '12px',
    border: '1px solid var(--border-input, #cbd5e1)',
    fontSize: '13px',
    background: 'var(--bg-input, #ffffff)',
    color: 'var(--text-primary, #0f172a)',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    lineHeight: '1.6',
    resize: 'vertical',
    outline: 'none'
  },
  fieldHelp: {
    fontSize: '11px',
    color: 'var(--text-muted, #94a3b8)'
  },
  row: {
    display: 'flex',
    gap: '16px'
  },
  jdHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  aiButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: 'linear-gradient(135deg, #a78bfa 0%, #6366f1 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 4px 10px rgba(99, 102, 241, 0.2)'
  },
  sparkleIcon: {
    width: '14px',
    height: '14px'
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 'auto'
  },
  submitBtn: {
    background: 'linear-gradient(135deg, var(--primary-color, #4f46e5) 0%, #3b82f6 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 10px 20px rgba(79, 70, 229, 0.25)',
    transition: 'all 0.2s'
  },

  // LIST VIEW STYLES
  listViewContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  filterBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '14px',
    background: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    padding: '16px 20px',
    borderRadius: '16px',
    border: '1px solid var(--border-light, #e2e8f0)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
  },
  searchBox: {
    position: 'relative',
    flex: '1 1 300px',
    display: 'flex',
    alignItems: 'center'
  },
  searchIcon: {
    position: 'absolute',
    left: '12px',
    width: '18px',
    height: '18px',
    color: 'var(--text-muted, #94a3b8)'
  },
  searchInput: {
    width: '100%',
    padding: '10px 36px 10px 38px',
    borderRadius: '10px',
    border: '1px solid var(--border-input, #cbd5e1)',
    fontSize: '13px',
    background: 'var(--bg-input, #ffffff)',
    color: 'var(--text-primary, #0f172a)',
    outline: 'none',
    boxSizing: 'border-box'
  },
  clearSearchBtn: {
    position: 'absolute',
    right: '10px',
    background: 'none',
    border: 'none',
    color: 'var(--text-muted, #94a3b8)',
    cursor: 'pointer',
    fontSize: '13px'
  },
  filterControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  filterSelect: {
    padding: '10px 14px',
    borderRadius: '10px',
    border: '1px solid var(--border-input, #cbd5e1)',
    fontSize: '13px',
    background: 'var(--bg-input, #ffffff)',
    color: 'var(--text-primary, #0f172a)',
    outline: 'none',
    cursor: 'pointer'
  },
  newJdBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 16px',
    borderRadius: '10px',
    border: 'none',
    background: 'var(--primary-color, #4f46e5)',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)',
    transition: 'all 0.2s'
  },
  btnIcon: {
    width: '16px',
    height: '16px'
  },
  loader: {
    padding: '40px',
    textAlign: 'center',
    color: 'var(--text-muted, #94a3b8)',
    fontSize: '14px'
  },
  emptyState: {
    padding: '48px 24px',
    textAlign: 'center',
    background: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    borderRadius: '16px',
    border: '1px dashed var(--border-light, #cbd5e1)'
  },
  emptyIcon: {
    fontSize: '40px',
    marginBottom: '12px'
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: '700',
    margin: '0 0 8px 0',
    color: 'var(--text-primary, #0f172a)'
  },
  emptyText: {
    fontSize: '14px',
    color: 'var(--text-muted, #64748b)',
    margin: '0 0 20px 0',
    maxWidth: '450px',
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  emptyActionBtn: {
    padding: '10px 20px',
    borderRadius: '10px',
    border: 'none',
    background: 'var(--primary-color, #4f46e5)',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  jdCardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
    gap: '20px'
  },
  cardItem: {
    background: 'var(--bg-card-glass, rgba(255, 255, 255, 0.85))',
    backdropFilter: 'blur(12px)',
    border: '1px solid var(--border-light, #e2e8f0)',
    borderRadius: '18px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.03)',
    transition: 'all 0.2s',
    boxSizing: 'border-box'
  },
  cardTopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px'
  },
  cardTitleWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  cardTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '700',
    color: 'var(--text-primary, #0f172a)',
    lineHeight: '1.3'
  },
  projectBadgeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: 'var(--primary-color, #4f46e5)'
  },
  projectIcon: {
    width: '14px',
    height: '14px'
  },
  cardProject: {
    fontSize: '12px',
    fontWeight: '600'
  },
  statusBadge: {
    padding: '3px 9px',
    borderRadius: '8px',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.03em',
    borderWidth: '1px',
    borderStyle: 'solid',
    whiteSpace: 'nowrap'
  },
  cardSpecsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
    padding: '12px',
    background: 'rgba(241, 245, 249, 0.5)',
    borderRadius: '12px'
  },
  specItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  specLabel: {
    fontSize: '10px',
    fontWeight: '700',
    color: 'var(--text-muted, #94a3b8)',
    letterSpacing: '0.04em'
  },
  specValue: {
    fontSize: '12px',
    fontWeight: '600',
    color: 'var(--text-primary, #1e293b)'
  },
  skillsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px'
  },
  skillTag: {
    padding: '3px 8px',
    borderRadius: '6px',
    background: 'rgba(79, 70, 229, 0.08)',
    color: 'var(--primary-color, #4f46e5)',
    fontSize: '11px',
    fontWeight: '600'
  },
  skillTagMore: {
    padding: '3px 8px',
    borderRadius: '6px',
    background: 'rgba(0, 0, 0, 0.05)',
    color: 'var(--text-muted, #64748b)',
    fontSize: '11px',
    fontWeight: '600'
  },
  jdSnippet: {
    fontSize: '12px',
    color: 'var(--text-muted, #64748b)',
    lineHeight: '1.5',
    background: 'rgba(0,0,0,0.02)',
    padding: '8px 10px',
    borderRadius: '8px',
    fontStyle: 'italic'
  },
  cardFooter: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: 'auto',
    paddingTop: '10px',
    borderTop: '1px solid var(--border-light, #f1f5f9)'
  },
  viewJdBtn: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid var(--border-input, #cbd5e1)',
    background: '#ffffff',
    color: 'var(--text-primary, #334155)',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  publishBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    borderRadius: '8px',
    border: 'none',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#ffffff',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  screenBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    borderRadius: '8px',
    border: 'none',
    background: 'linear-gradient(135deg, var(--primary-color, #4f46e5) 0%, #3b82f6 100%)',
    color: '#ffffff',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  actionBtnIcon: {
    width: '14px',
    height: '14px'
  },

  // MODAL STYLES
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.55)',
    backdropFilter: 'blur(6px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px'
  },
  modalContainer: {
    background: '#ffffff',
    borderRadius: '20px',
    width: '100%',
    maxWidth: '720px',
    maxHeight: '88vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    overflow: 'hidden'
  },
  modalHeader: {
    padding: '20px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '1px solid #e2e8f0'
  },
  modalTitleBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  modalTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '800',
    color: '#0f172a'
  },
  modalSub: {
    margin: 0,
    fontSize: '12px',
    color: '#64748b'
  },
  modalCloseBtn: {
    background: '#f1f5f9',
    border: 'none',
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#64748b',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold'
  },
  modalBody: {
    padding: '20px 24px',
    overflowY: 'auto',
    flex: 1
  },
  modalSpecsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
    gap: '12px',
    background: '#f8fafc',
    padding: '14px',
    borderRadius: '12px',
    border: '1px solid #e2e8f0'
  },
  modalSpecBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  copyBtn: {
    padding: '4px 10px',
    borderRadius: '6px',
    border: '1px solid #cbd5e1',
    background: '#ffffff',
    fontSize: '11px',
    fontWeight: '600',
    color: '#475569',
    cursor: 'pointer'
  },
  jdContentBox: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '16px',
    maxHeight: '320px',
    overflowY: 'auto'
  },
  jdPre: {
    margin: 0,
    fontFamily: 'inherit',
    fontSize: '13px',
    color: '#1e293b',
    whiteSpace: 'pre-wrap',
    lineHeight: '1.6'
  },
  modalFooter: {
    padding: '16px 24px',
    borderTop: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    background: '#f8fafc'
  },
  closeBtn: {
    padding: '9px 18px',
    borderRadius: '10px',
    border: '1px solid #cbd5e1',
    background: '#ffffff',
    color: '#475569',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  publishModalBtn: {
    padding: '9px 20px',
    borderRadius: '10px',
    border: 'none',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  screenModalBtn: {
    padding: '9px 20px',
    borderRadius: '10px',
    border: 'none',
    background: 'linear-gradient(135deg, var(--primary-color, #4f46e5) 0%, #3b82f6 100%)',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer'
  }
};

export default JDBuilder;
